import { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import DOMPurify from 'dompurify';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBold,
  faItalic,
  faUnderline,
  faStrikethrough,
  faListUl,
  faListOl,
  faQuoteLeft,
  faLink,
  faAlignLeft,
  faAlignCenter,
  faAlignRight,
  faRotateLeft,
  faRotateRight,
} from '@fortawesome/free-solid-svg-icons';
import { cn } from '@/lib/cn';

interface Props {
  value: string;
  onChange: (html: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

interface ToolbarButtonProps {
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  title: string;
  children: React.ReactNode;
}

function ToolbarButton({ onClick, active, disabled, title, children }: ToolbarButtonProps) {
  return (
    <button
      type="button"
      onMouseDown={(e) => {
        e.preventDefault();
        if (!disabled) onClick();
      }}
      title={title}
      disabled={disabled}
      className={cn(
        'flex h-7 min-w-[28px] items-center justify-center rounded px-1.5 text-xs transition-colors',
        active ? 'bg-primary/20 text-primary' : 'text-foreground hover:bg-muted',
        disabled && 'opacity-40 cursor-not-allowed',
      )}
    >
      {children}
    </button>
  );
}

export function RichTextEditor({ value, onChange, disabled = false, placeholder }: Props) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ codeBlock: false }),
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Link.configure({ openOnClick: false }),
      Placeholder.configure({ placeholder: placeholder ?? 'Add step instructions…' }),
    ],
    content: value || '',
    editable: !disabled,
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML();
      onChange(DOMPurify.sanitize(html));
    },
  });

  // Sync external value changes
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (value !== current) {
      editor.commands.setContent(value || '', false);
    }
  }, [editor, value]);

  // Sync disabled state
  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled);
  }, [editor, disabled]);

  if (!editor) return null;

  const setLink = async (): Promise<void> => {
    const prev = editor.getAttributes('link').href as string | undefined;
    const { isConfirmed, value } = await Swal.fire<string>({
      title: 'Insert Link',
      input: 'url',
      inputValue: prev ?? '',
      inputPlaceholder: 'https://example.com',
      showCancelButton: true,
      confirmButtonText: 'Apply',
      cancelButtonText: prev ? 'Remove Link' : 'Cancel',
      confirmButtonColor: 'hsl(74 40% 40%)',
      cancelButtonColor: prev ? 'hsl(0 84% 60%)' : 'hsl(74 15% 45%)',
      reverseButtons: true,
      customClass: { popup: 'rounded-xl' },
      inputValidator: (v) => {
        if (v && !/^https?:\/\/.+/.test(v))
          return 'Enter a valid URL starting with http:// or https://';
      },
    });
    if (isConfirmed) {
      if (!value) {
        editor.chain().focus().unsetLink().run();
      } else {
        editor.chain().focus().setLink({ href: value }).run();
      }
    } else if (prev) {
      editor.chain().focus().unsetLink().run();
    }
  };

  return (
    <div className={cn('rounded-lg border border-input overflow-hidden', disabled && 'opacity-60')}>
      {/* Toolbar */}
      {!disabled && (
        <div className="flex flex-wrap items-center gap-0.5 border-b border-border bg-muted/30 px-2 py-1.5">
          <ToolbarButton onClick={() => editor.chain().focus().undo().run()} title="Undo">
            <FontAwesomeIcon icon={faRotateLeft} className="h-3 w-3" />
          </ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().redo().run()} title="Redo">
            <FontAwesomeIcon icon={faRotateRight} className="h-3 w-3" />
          </ToolbarButton>

          <div className="mx-1 h-5 w-px bg-border" />

          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBold().run()}
            active={editor.isActive('bold')}
            title="Bold"
          >
            <FontAwesomeIcon icon={faBold} className="h-3 w-3" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleItalic().run()}
            active={editor.isActive('italic')}
            title="Italic"
          >
            <FontAwesomeIcon icon={faItalic} className="h-3 w-3" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            active={editor.isActive('underline')}
            title="Underline"
          >
            <FontAwesomeIcon icon={faUnderline} className="h-3 w-3" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleStrike().run()}
            active={editor.isActive('strike')}
            title="Strikethrough"
          >
            <FontAwesomeIcon icon={faStrikethrough} className="h-3 w-3" />
          </ToolbarButton>

          <div className="mx-1 h-5 w-px bg-border" />

          <ToolbarButton
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            active={editor.isActive('heading', { level: 2 })}
            title="Heading 2"
          >
            <span className="text-[10px] font-bold">H2</span>
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            active={editor.isActive('heading', { level: 3 })}
            title="Heading 3"
          >
            <span className="text-[10px] font-bold">H3</span>
          </ToolbarButton>

          <div className="mx-1 h-5 w-px bg-border" />

          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            active={editor.isActive('bulletList')}
            title="Bullet list"
          >
            <FontAwesomeIcon icon={faListUl} className="h-3 w-3" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            active={editor.isActive('orderedList')}
            title="Ordered list"
          >
            <FontAwesomeIcon icon={faListOl} className="h-3 w-3" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            active={editor.isActive('blockquote')}
            title="Blockquote"
          >
            <FontAwesomeIcon icon={faQuoteLeft} className="h-3 w-3" />
          </ToolbarButton>

          <div className="mx-1 h-5 w-px bg-border" />

          <ToolbarButton
            onClick={() => editor.chain().focus().setTextAlign('left').run()}
            active={editor.isActive({ textAlign: 'left' })}
            title="Align left"
          >
            <FontAwesomeIcon icon={faAlignLeft} className="h-3 w-3" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().setTextAlign('center').run()}
            active={editor.isActive({ textAlign: 'center' })}
            title="Align center"
          >
            <FontAwesomeIcon icon={faAlignCenter} className="h-3 w-3" />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().setTextAlign('right').run()}
            active={editor.isActive({ textAlign: 'right' })}
            title="Align right"
          >
            <FontAwesomeIcon icon={faAlignRight} className="h-3 w-3" />
          </ToolbarButton>

          <div className="mx-1 h-5 w-px bg-border" />

          <ToolbarButton onClick={setLink} active={editor.isActive('link')} title="Insert link">
            <FontAwesomeIcon icon={faLink} className="h-3 w-3" />
          </ToolbarButton>
        </div>
      )}

      {/* Editor area */}
      <EditorContent
        editor={editor}
        className={cn(
          'prose prose-sm max-w-none px-3 py-2.5 text-foreground dark:prose-invert',
          '[&_.ProseMirror]:outline-none [&_.ProseMirror]:min-h-[120px]',
          '[&_.ProseMirror_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)]',
          '[&_.ProseMirror_p.is-editor-empty:first-child::before]:text-muted-foreground',
          '[&_.ProseMirror_p.is-editor-empty:first-child::before]:float-left',
          '[&_.ProseMirror_p.is-editor-empty:first-child::before]:pointer-events-none',
          '[&_.ProseMirror_a]:text-primary [&_.ProseMirror_a]:underline',
          '[&_.ProseMirror_h2]:text-base [&_.ProseMirror_h2]:font-bold',
          '[&_.ProseMirror_h3]:text-sm [&_.ProseMirror_h3]:font-semibold',
        )}
      />
    </div>
  );
}
